import React from "react";
import { CheckIcon, CloseIcon } from "@/components/icons";
import { usePushSetup } from "./usePushSetup";
import { installPlatform, isPushSupported } from "./pushSupport";
import { isStandaloneDisplayMode } from "../loginCode";

interface NotificationSettingsCardProps {
  clientId: string;
}

/**
 * Persistent notification controls on the portal dashboard (below the
 * social accounts section). This is the recovery path the transient
 * permission card cannot provide: once the browser prompt is denied,
 * the site can never re-ask programmatically — all we can do is show
 * the user where to flip it back manually (DD-018/TD-003 family).
 */
export const NotificationSettingsCard: React.FC<NotificationSettingsCardProps> = ({
  clientId,
}) => {
  const { permission, subscribed, subscribe, enable } = usePushSetup(clientId);

  const standalone = isStandaloneDisplayMode();
  const platform = installPlatform(navigator.userAgent, standalone);

  if (!isPushSupported()) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
        {platform === "ios" ? (
          <>
            Su iPhone le notifiche funzionano solo dall'app installata.
            Aggiungi prima l'app alla schermata Home (Condividi →
            "Aggiungi alla schermata Home"), poi attiva le notifiche da qui.
          </>
        ) : (
          <>Questo browser non supporta le notifiche push.</>
        )}
      </div>
    );
  }

  if (permission === "granted") {
    // Granted but not yet registered with the backend: the subscribe can
    // fail (server misconfiguration, network) while permission stays
    // granted — never present that as "active".
    if (!subscribed) {
      return (
        <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 rounded-lg">
          <p className="text-sm text-amber-900">
            Notifiche consentite, ma questo dispositivo non risulta ancora
            registrato.
          </p>
          <button
            type="button"
            onClick={() => void subscribe()}
            className="shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-900">
          Avvisi sui nuovi contenuti da approvare
        </p>
        <div className="flex items-center gap-1 text-green-600">
          <CheckIcon className="w-4 h-4" />
          <span className="text-xs font-medium">Attive</span>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className="p-3 bg-gray-50 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">
            Avvisi sui nuovi contenuti da approvare
          </p>
          <div className="flex items-center gap-1 text-gray-400">
            <CloseIcon className="w-4 h-4" />
            <span className="text-xs font-medium">Bloccate</span>
          </div>
        </div>
        <p className="text-xs text-gray-600">
          {standalone ? (
            <>
              Le notifiche sono bloccate. Aprile dalle impostazioni del
              telefono: Impostazioni → Notifiche → BeNerdy → Consenti
              notifiche, poi riapri l'app.
            </>
          ) : (
            <>
              Le notifiche sono bloccate per questo sito. Clicca l'icona
              accanto all'indirizzo del browser → Notifiche → Consenti,
              poi ricarica la pagina.
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
      <p className="text-sm text-gray-700">
        Ricevi un avviso quando ci sono nuovi contenuti da approvare.
      </p>
      <button
        type="button"
        onClick={() => void enable()}
        className="shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
      >
        Attiva notifiche
      </button>
    </div>
  );
};
